import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Users, UserCheck, Activity, Calendar, PlayCircle, CheckCircle2, AlertTriangle,
  Truck, Wrench, Layers, RefreshCw, Stethoscope, MapPin, Heart,
} from "lucide-react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_CENTER, VISIT_STATUS_COLORS, spreadOverlaps } from "@/pages/fleet/constants";
import { useMapboxToken } from "@/hooks/useMapboxToken";

const REFRESH_MS = 30000;

/* ── KPI Tile (read-only, reference style) ────────────────── */
function KpiTile({ icon: Icon, label, value, accent, sub, subColor, testId }) {
  return (
    <div data-testid={testId} className="hah-card p-4">
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${accent}1A`, color: accent }}>
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <div className="text-[28px] leading-none font-bold text-[#1F2937]">{value ?? "—"}</div>
          <div className="text-[12px] font-semibold text-[#475569] mt-1.5 leading-tight">{label}</div>
        </div>
      </div>
      {sub && (
        <div className="text-[11px] mt-3 truncate" style={{ color: subColor || "#94A3B8" }}>{sub}</div>
      )}
    </div>
  );
}

/* ── Wide vehicle stat ────────────────────────────────────── */
function VehicleStat({ icon: Icon, value, label, sub, accent, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-4 px-6 py-5 flex-1">
      <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${accent}1A`, color: accent }}>
        <Icon className="w-6 h-6" strokeWidth={2.2} />
      </span>
      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-[26px] leading-none font-bold text-[#1F2937]">{value ?? "—"}</span>
          <span className="text-[13px] font-semibold text-[#374151]">{label}</span>
        </div>
        <div className="text-[11px] text-[#94A3B8] mt-1">{sub}</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const c = VISIT_STATUS_COLORS[status] || VISIT_STATUS_COLORS.cancelled || { text: "#64748B", label: status };
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-medium"
          style={{ background: `${c.text}14`, color: c.text }}>
      {c.label}
    </span>
  );
}

const fmtTime = (ts) => {
  if (!ts) return null;
  try { return new Date(ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return null; }
};

/* ── Main ─────────────────────────────────────────────────── */
export default function FleetDashboard() {
  const MAPBOX_TOKEN = useMapboxToken();
  const [data, setData] = useState({ kpis: { workforce: {}, visits: {}, vehicles: {} } });
  const [visits, setVisits] = useState([]);
  const [mapData, setMapData] = useState({ clinicians: [], patients: [], vehicles: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const mapRef = useRef(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [dRes, vRes, mRes] = await Promise.all([
        api.get("/fleet/dashboard"),
        api.get("/fleet/visits?page_size=50"),
        api.get("/fleet/map"),
      ]);
      setData(dRes.data);
      setVisits(vRes.data?.items || []);
      setMapData(mRes.data || { clinicians: [], patients: [], vehicles: [] });
      setUpdatedAt(new Date());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const k = data.kpis || {};
  const wf = k.workforce || {};
  const vs = k.visits || {};
  const vh = k.vehicles || {};
  const perf = k.performance || {};
  const cov = k.coverage || {};
  const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}% of clinicians` : null);

  const activeVisits = (vs.en_route || 0) + (vs.in_progress || 0);
  const activeVehicles = Math.max(0, (vh.total || 0) - (vh.maintenance || 0));

  /* Distinct clinicians currently on an active visit (en route / in progress) */
  const onActiveVisit = useMemo(() => {
    const s = new Set();
    visits.forEach((v) => {
      if (["en_route", "in_progress", "arrived"].includes(v.status)) {
        (v.clinician_ids || [v.clinician_id]).filter(Boolean).forEach((id) => s.add(id));
      }
    });
    return s.size;
  }, [visits]);

  /* Map patient_id -> city for the activity table Location column */
  const patientCity = useMemo(() => {
    const m = {};
    (mapData.patients || []).forEach((p) => { if (p.patient_id) m[p.patient_id] = p.city; });
    return m;
  }, [mapData]);

  /* Recent visit activity (newest first) */
  const recent = useMemo(() => {
    const rank = (s) => ({ in_progress: 0, en_route: 1, assigned: 2, delayed: 3, completed: 4 }[s] ?? 5);
    return [...visits]
      .sort((a, b) => {
        const t = (b.updated_at || b.scheduled_at || b.created_at || "").localeCompare(a.updated_at || a.scheduled_at || a.created_at || "");
        return rank(a.status) - rank(b.status) || t;
      })
      .slice(0, 6);
  }, [visits]);

  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: undefined, day: "numeric", month: "short", year: "numeric" });

  /* Spread co-located markers exactly like the Live Operations Map */
  const displayClinicians = useMemo(() => spreadOverlaps(mapData.clinicians), [mapData.clinicians]);
  const displayPatients = useMemo(() => spreadOverlaps(mapData.patients), [mapData.patients]);
  const displayVehicles = useMemo(() => spreadOverlaps(mapData.vehicles), [mapData.vehicles]);

  /* Use the SAME initial view as the Live Operations Map (Riyadh, city zoom)
     so both maps present the live data identically. */
  const center = useMemo(() => ({
    longitude: DEFAULT_CENTER?.longitude || 46.6753,
    latitude: DEFAULT_CENTER?.latitude || 24.7136,
    zoom: DEFAULT_CENTER?.zoom || 11,
  }), []);

  return (
    <div data-testid="fleet-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Fleet Command Center</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">Real-time overview of home visit operations</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#E5E7EB] text-[13px] text-[#374151] bg-white">
            <Calendar className="w-4 h-4 text-[#6B7280]" /> Today, {dateLabel}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-[#6B7280]">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Updated {updatedAt ? fmtTime(updatedAt) : "…"}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <>
          {/* KPI strip — every tile maps to a tracked backend field */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-3">
            <KpiTile testId="fleet-kpi-active-clinicians" icon={Users} accent="#0B7E8C"
                     value={(wf.total || 0) - (wf.offline || 0)} label="Active Clinicians"
                     sub={`${wf.ready ?? 0} on shift`} subColor="#10B981" />
            <KpiTile testId="fleet-kpi-available" icon={UserCheck} accent="#10B981"
                     value={wf.available} label="Available for Dispatch" sub={pct(wf.available, wf.total)} />
            <KpiTile testId="fleet-kpi-on-visit" icon={Activity} accent="#8B5CF6"
                     value={onActiveVisit} label="On Active Visit" sub="En route / in progress" />
            <KpiTile testId="fleet-kpi-unassigned" icon={Calendar} accent="#F59E0B"
                     value={vs.unassigned} label="Unassigned Visits"
                     sub={vs.unassigned ? "Awaiting dispatch" : "All dispatched"}
                     subColor={vs.unassigned ? "#F59E0B" : "#94A3B8"} />
            <KpiTile testId="fleet-kpi-active-visits" icon={PlayCircle} accent="#3B82F6"
                     value={activeVisits} label="Active Visits" sub="In progress / en route" />
            <KpiTile testId="fleet-kpi-completed" icon={CheckCircle2} accent="#10B981"
                     value={vs.completed} label="Completed Visits" sub="Cumulative" />
            <KpiTile testId="fleet-kpi-delayed" icon={AlertTriangle} accent="#EF4444"
                     value={vs.delayed} label="Delayed Visits"
                     sub={vs.delayed ? "Needs attention" : "On schedule"}
                     subColor={vs.delayed ? "#EF4444" : "#94A3B8"} />
          </div>

          {/* Vehicle strip */}
          <div className="hah-card flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-[#E5E7EB] mb-3">
            <VehicleStat testId="fleet-kpi-active-vehicles" icon={Truck} accent="#3B82F6"
                         value={activeVehicles} label="Operational Vehicles" sub={`${vh.assigned ?? 0} on the road`} />
            <VehicleStat testId="fleet-kpi-maintenance" icon={Wrench} accent="#EF4444"
                         value={vh.maintenance} label="Vehicles in Maintenance" sub="Unavailable" />
          </div>

          {/* Operational performance + coverage — tracked by the fleet engine */}
          <div className="hah-card px-2 py-3 mb-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-[#E5E7EB]"
               data-testid="fleet-performance">
            <MiniStat label="Avg Travel" value={perf.avg_travel_min != null ? `${perf.avg_travel_min}m` : "—"} />
            <MiniStat label="Avg Visit" value={perf.avg_visit_min != null ? `${perf.avg_visit_min}m` : "—"} />
            <MiniStat label="Utilization" value={perf.utilization_pct != null ? `${perf.utilization_pct}%` : "—"} />
            <MiniStat label="SLA Compliance" value={perf.sla_compliance_pct != null ? `${perf.sla_compliance_pct}%` : "—"} />
            <MiniStat label="AI Acceptance" value={perf.ai_acceptance_pct != null ? `${perf.ai_acceptance_pct}%` : "—"} />
            <MiniStat label="Patients at Home" value={cov.patients_at_home ?? "—"} />
            <MiniStat label="Cities Covered" value={(cov.by_city || []).filter((c) => c.city !== "Unspecified").length || "—"} />
          </div>

          {/* Live Operations Map */}
          <div className="hah-card p-5 mb-6" data-testid="fleet-live-map">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[15px] font-semibold text-[#1F2937]">Live Operations Map</h2>
              <span className="inline-flex items-center gap-1.5 text-[12px] text-[#6B7280] px-2.5 py-1 rounded-md border border-[#E5E7EB]">
                <Layers className="w-3.5 h-3.5" /> Layers
              </span>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 mb-3 text-[12px] text-[#475569]">
              <LegendDot color="#0B7E8C" label="Patient" />
              <LegendDot color="#3B82F6" label="Clinician" />
              <LegendDot color="#A855F7" label="Active Visit" />
              <LegendDot color="#10B981" label="Completed" />
              <LegendDot color="#EF4444" label="Delayed" />
            </div>
            <div className="relative w-full h-[420px] rounded-xl overflow-hidden border border-[#E5E7EB]">
              {MAPBOX_TOKEN ? (
                <Map
                  ref={mapRef}
                  mapboxAccessToken={MAPBOX_TOKEN}
                  initialViewState={center}
                  mapStyle="mapbox://styles/mapbox/light-v11"
                  attributionControl={false}
                  style={{ width: "100%", height: "100%" }}
                >
                  <NavigationControl position="bottom-right" showCompass={false} />
                  {displayClinicians.map((c) => (
                    <Marker key={`c-${c.clinician_id}`} longitude={c._lng} latitude={c._lat} anchor="center">
                      <Pin color="#3B82F6"><Stethoscope className="w-3.5 h-3.5 text-white" /></Pin>
                    </Marker>
                  ))}
                  {displayPatients.map((p) => (
                    <Marker key={`p-${p.patient_id}`} longitude={p._lng} latitude={p._lat} anchor="center">
                      <Pin color={p.has_active_visit ? "#A855F7" : "#0B7E8C"}>
                        {p.has_active_visit ? <Heart className="w-3.5 h-3.5 text-white" /> : <MapPin className="w-3.5 h-3.5 text-white" />}
                      </Pin>
                    </Marker>
                  ))}
                  {displayVehicles.map((v) => (
                    <Marker key={`v-${v.id}`} longitude={v._lng} latitude={v._lat} anchor="center">
                      <Pin color="#475569"><Truck className="w-3.5 h-3.5 text-white" /></Pin>
                    </Marker>
                  ))}
                </Map>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-[#94A3B8]">
                  Map unavailable — Mapbox token not configured.
                </div>
              )}
            </div>
          </div>

          {/* Recent Visit Activity */}
          <div className="hah-card p-5 mb-4" data-testid="fleet-recent-activity">
            <h2 className="text-[15px] font-semibold text-[#1F2937] mb-4">Recent Visit Activity</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-[#94A3B8] border-b border-[#E5E7EB]">
                    <th className="text-left font-semibold py-2.5 pr-4">Patient</th>
                    <th className="text-left font-semibold py-2.5 pr-4">Visit ID</th>
                    <th className="text-left font-semibold py-2.5 pr-4">Clinician</th>
                    <th className="text-left font-semibold py-2.5 pr-4">Status</th>
                    <th className="text-left font-semibold py-2.5 pr-4">ETA / Started</th>
                    <th className="text-left font-semibold py-2.5">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-[#94A3B8]">No visit activity.</td></tr>
                  )}
                  {recent.map((v) => (
                    <tr key={v.id} className="border-b border-[#F1F5F9] last:border-0">
                      <td className="py-3 pr-4">
                        <span className="inline-flex items-center gap-2 text-[#1F2937] font-medium">
                          <Users className="w-3.5 h-3.5 text-[#0B7E8C]" /> {v.patient_name || "—"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-[#6B7280] tabular-nums">{v.visit_code || `HV-${(v.id || "").slice(0, 8)}`}</td>
                      <td className="py-3 pr-4 text-[#374151]">{(v.clinician_names || [v.clinician_name]).filter(Boolean).join(", ") || "—"}</td>
                      <td className="py-3 pr-4"><StatusPill status={v.status} /></td>
                      <td className="py-3 pr-4 text-[#6B7280]">{etaLabel(v)}</td>
                      <td className="py-3 text-[#6B7280]">{v.patient_address || v.city || patientCity[v.patient_id] || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /> {label}
    </span>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="px-3 py-1 text-center">
      <div className="text-[18px] font-bold text-[#1F2937] leading-none">{value}</div>
      <div className="text-[10.5px] uppercase tracking-wide text-[#94A3B8] mt-1">{label}</div>
    </div>
  );
}

function Pin({ color, children }) {
  return (
    <span className="w-7 h-7 rounded-full flex items-center justify-center shadow-md ring-2 ring-white"
          style={{ background: color }}>
      {children}
    </span>
  );
}

function etaLabel(v) {
  if (v.status === "completed") {
    const t = fmtTime(v.completed_at || v.updated_at);
    return t ? `Completed ${t}` : "Completed";
  }
  if (v.status === "in_progress" || v.status === "arrived") {
    const t = fmtTime(v.started_at || v.actual_start || v.updated_at);
    return t ? `Started ${t}` : "In progress";
  }
  if (v.sla_breached || v.status === "delayed") return "Delayed";
  const t = fmtTime(v.scheduled_at);
  return t ? `ETA ${t}` : "—";
}
